import ApplicationController from '../application_controller.js';
import StimulusReflex from 'stimulus_reflex';
import consumer from '../../channels/consumer';
import 'emoji-picker-element';

export default class extends ApplicationController {
  static targets = ["messages", "input", "emojiPicker"];
  static values = {
    myContactId: Number,
    sessionId: String,
    realSessionId: String,
    simulationContactMap: Object
  };

  connect() {
    super.connect();
    StimulusReflex.register(this);
    this.scrollToBottom();
    this.subscribeToChannel();
    this.initEmojiPicker();

    // Track seen message IDs to prevent duplicates
    this.seenMessageIds = new Set();

    // Mark existing messages as seen
    this.element.querySelectorAll('[data-message-id]').forEach(el => {
      this.seenMessageIds.add(parseInt(el.dataset.messageId));
    });
  }

  initEmojiPicker() {
    if (!this.hasEmojiPickerTarget) return;

    // Create the emoji picker element if not already present
    if (!this.emojiPickerTarget.querySelector('emoji-picker')) {
      const picker = document.createElement('emoji-picker');
      picker.addEventListener('emoji-click', this.handleEmojiSelect.bind(this));
      this.emojiPickerTarget.appendChild(picker);
    }

    // Close picker when clicking outside
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this);
    document.addEventListener('click', this.boundCloseOnClickOutside);
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.realSubscription) {
      this.realSubscription.unsubscribe();
    }
    if (this.boundCloseOnClickOutside) {
      document.removeEventListener('click', this.boundCloseOnClickOutside);
    }
  }

  toggleEmojiPicker(event) {
    event.stopPropagation();
    if (!this.hasEmojiPickerTarget) return;

    this.emojiPickerTarget.classList.toggle('d-none');
  }

  handleEmojiSelect(event) {
    if (!this.hasInputTarget) return;

    const emoji = event.detail.unicode;
    const input = this.inputTarget;
    const start = input.selectionStart;
    const end = input.selectionEnd;

    // Insert emoji at cursor position
    input.value = input.value.substring(0, start) + emoji + input.value.substring(end);

    // Move cursor after the inserted emoji
    input.selectionStart = input.selectionEnd = start + emoji.length;
    input.focus();

    // Hide picker after selection
    this.emojiPickerTarget.classList.add('d-none');
  }

  closeOnClickOutside(event) {
    if (!this.hasEmojiPickerTarget) return;

    // Don't close if clicking inside the picker or the toggle button
    if (this.emojiPickerTarget.contains(event.target) ||
        event.target.closest('[data-action*="toggleEmojiPicker"]')) {
      return;
    }

    this.emojiPickerTarget.classList.add('d-none');
  }

  subscribeToChannel() {
    const sessionId = this.sessionIdValue;
    const realSessionId = this.realSessionIdValue;
    const self = this;

    // Subscribe to the fake session ID for UI updates
    this.subscription = consumer.subscriptions.create(
      { channel: "LiveCallUpdateChannel", session_id: sessionId },
      {
        received: (data) => {
          if (data.message === 'new_chat_message') {
            self.handleNewMessage(data.chat_message);
          }
        }
      }
    );

    // Also subscribe to real session ID if different (for chat messages)
    if (realSessionId && realSessionId !== sessionId) {
      this.realSubscription = consumer.subscriptions.create(
        { channel: "LiveCallUpdateChannel", session_id: realSessionId },
        {
          received: (data) => {
            if (data.message === 'new_chat_message') {
              self.handleNewMessage(data.chat_message);
            }
          }
        }
      );
    }
  }

  handleNewMessage(messageData) {
    // Deduplicate messages (can receive from both session channels)
    if (this.seenMessageIds.has(messageData.id)) {
      return;
    }
    this.seenMessageIds.add(messageData.id);

    // Remove "no messages" placeholder if present
    const placeholder = document.getElementById('no-messages-placeholder');
    if (placeholder) {
      placeholder.remove();
    }

    // Check if this is our own message
    // In hybrid simulation: my_contact_id is fake (1001), message.contact_id is real
    // Need to check if message.contact_id maps to our fake ID
    const map = this.simulationContactMapValue || {};
    const myRealContactId = map[this.myContactIdValue] || this.myContactIdValue;
    const isOwnMessage = messageData.contact_id === myRealContactId;

    const messageHtml = this.buildMessageHtml(messageData, isOwnMessage);

    if (this.hasMessagesTarget) {
      this.messagesTarget.insertAdjacentHTML('beforeend', messageHtml);
      this.scrollToBottom();
    }

    // Auto-open sidebar if closed and it's not our own message
    if (!isOwnMessage) {
      this.openSidebarIfClosed();
    }
  }

  openSidebarIfClosed() {
    const sidebar = document.getElementById('Sidebar');
    if (sidebar && sidebar.classList.contains('sidebar-closed')) {
      // Dispatch event for sidebar controller to handle
      this.dispatch('requestOpen', { bubbles: true });
    }
  }

  buildMessageHtml(message, isOwn) {
    const alignment = isOwn ? 'text-end' : '';
    const bgClass = isOwn ? 'bg-secondary text-white' : 'bg-light';
    const timeClass = isOwn ? 'text-white-50' : 'text-muted';
    const senderHtml = isOwn ? '' : `<small class="d-block fw-bold text-muted">${this.escapeHtml(message.sender_name)}</small>`;

    const time = new Date(message.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

    return `
      <div class="chat-message mb-2 ${alignment}" data-message-id="${message.id}" data-contact-id="${message.contact_id}">
        <div class="d-inline-block p-2 rounded-3 ${bgClass}" style="max-width: 85%;">
          ${senderHtml}
          <span class="message-content">${this.linkifyText(message.content)}</span>
          <small class="d-block ${timeClass}" style="font-size: 0.7rem;">${time}</small>
        </div>
      </div>
    `;
  }

  sendMessage(event) {
    event.preventDefault();

    if (!this.hasInputTarget) return;

    const content = this.inputTarget.value.trim();
    if (!content) return;

    // Clear input immediately for better UX
    this.inputTarget.value = '';

    // Send via reflex
    this.stimulate('LiveCall#send_chat_message', content);
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
    }
  }

  linkifyText(text) {
    const urlRegex = /(https?:\/\/[^\s<]+)/g;
    return this.escapeHtml(text).replace(urlRegex, '<a href="$1" target="_blank" rel="noopener" class="text-decoration-underline">$1</a>');
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
